import { Input } from "@/components/ui/input";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { LayoutGrid, List, Share2, Plus, FolderInput } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { CreatePortfolioDialog } from "@/components/portfolio/CreatePortfolioDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { ComboButton } from "@/components/ui/ComboButton";
import { useToast } from "@/components/ui/use-toast";
import { ImportPortfolioDialog } from "@/components/portfolio/ImportPortfolioDialog";

type PortfolioViewMode = 'cards' | 'table';

interface PortfolioHeaderProps {
  search: string;
  onSearchChange: (value: string) => void;
  onCreatePortfolio: (name: string, type: string) => void;
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  viewMode: PortfolioViewMode;
  onViewModeChange: (value: PortfolioViewMode) => void;
  hideControls?: boolean;
}

export function PortfolioHeaderNew({ search, onSearchChange, onCreatePortfolio, typeFilter, onTypeFilterChange, viewMode, onViewModeChange, hideControls }: PortfolioHeaderProps) {
  const { userRole, isLoading } = useUserRole();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [openCreate, setOpenCreate] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  
  if (isLoading) {
    return <div className="h-24 animate-pulse bg-muted rounded-lg mb-6" />;
  }

  return (
    <div className="space-y-6">
      {/* Title, subtitle, and button area */}
      <div className="flex flex-col mb-2">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Portfolios</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage and track your investment portfolios
            </p>
          </div>
          <div className="flex items-center gap-2 md:flex-shrink-0 overflow-visible">
            <ComboButton
              label="New Portfolio"
              icon={<Plus className="h-4 w-4" />}
              onPrimaryClick={() => setOpenCreate(true)}
              size="sm"
              variant="default"
              className={isMobile ? "w-full" : ""}
              menuItems={(() => {
                  const canImport = ["sys_admin", "manager", "manager_admin", "advisor"].includes(userRole);
                  const items = [
                    {
                      label: "Share Portfolio",
                      onClick: () => {
                        (async () => {
                          try {
                            const shareData = {
                              title: "Portfolios",
                              text: "Check out my portfolios on Superalts",
                              url: window.location.origin + "/portfolios",
                            };
                            if (navigator.share) {
                              await navigator.share(shareData);
                            } else if (navigator.clipboard?.writeText) {
                              await navigator.clipboard.writeText(shareData.url);
                              toast({ title: "Link copied", description: "Portfolio link copied to clipboard" });
                            } else {
                              toast({ title: "Share unavailable", description: "Cannot share from this device", variant: "destructive" });
                            }
                          } catch (e) {
                            toast({ title: "Share cancelled", description: "" });
                          }
                        })();
                      },
                      icon: <Share2 className="h-4 w-4" />,
                    },
                  ];
                  if (canImport) {
                    items.push({
                      label: "Import Portfolio",
                      onClick: () => setOpenImport(true),
                      icon: <FolderInput className="h-4 w-4" />,
                    });
                  }
                  return items;
                })()}
            />
            {/* Controlled dialog (trigger hidden) */}
            <CreatePortfolioDialog
              onCreatePortfolio={onCreatePortfolio}
              hideTrigger
              open={openCreate}
              onOpenChange={setOpenCreate}
            />
            <ImportPortfolioDialog
              open={openImport}
              onOpenChange={setOpenImport}
            />
          </div>
        </div>
      </div>

      {/* Search, view toggle, and type filter (wrap on small widths) */}
      {!hideControls && (
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-3 w-full">
        {/* View Toggle */}
        <div className="order-1 md:order-none">
          <ToggleGroup 
            type="single" 
            value={viewMode} 
            onValueChange={(value) => value && onViewModeChange(value as PortfolioViewMode)}
            className=""
          >
            <ToggleGroupItem value="cards" aria-label="Card view" className="h-10 w-10 p-0">
              <LayoutGrid className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="table" aria-label="Table view" className="h-10 w-10 p-0">
              <List className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="w-full">
          <Input
            placeholder="Search portfolios..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full min-h-[44px]"
          />
        </div>

        <div className="md:justify-self-end">
          <Select value={typeFilter} onValueChange={onTypeFilterChange}>
            <SelectTrigger className="min-h-[44px] w-full sm:w-[180px] md:w-[200px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className="z-50 bg-popover">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="model">Model Portfolio</SelectItem>
              <SelectItem value="personal">Personal Portfolio</SelectItem>
              <SelectItem value="client">Client Portfolio</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      )}
    </div>
  );
}
